import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Bookmark, CornerDownLeft, Search, Settings, ShieldCheck, User } from 'lucide-react';
import { useSession } from '@/api/auth';
import { useIsAdmin } from '@/api/hooks';
import { CosmosMark } from '@/components/CosmosMark';
import { MODULES, type ModuleDef } from '@/lib/modules';
import { ADMIN_SECTIONS, MODULE_MENUS, type NavGroup } from '@/lib/navigation';
import { pageDestinations, searchPages } from '@/lib/pageSearch';
import { ROUTES, companyPath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { useCompanies, type CompanyLinks } from '@/api/insights';
import { TierBox, TIER_LABEL, TIER_ORDER } from '@/components/TierBox';

/**
 * Home — Cosmos's front door. One card per module with every page it has (the same lists the
 * sidebar renders, from lib/navigation.ts), a "jump to" filter across all of them,
 * the client list with each client's tier (Autotask Classification, decided 2026-10-16), and the planned
 * modules underneath. The client list is its only API call (the company index the client picker uses).
 */

const ACCOUNT_LINKS = [
  { to: ROUTES.account.info, label: 'Account info', icon: User },
  { to: ROUTES.account.noteTemplates, label: 'Note templates', icon: Settings },
  { to: ROUTES.account.bookmarks, label: 'Bookmarks', icon: Bookmark },
  { to: ROUTES.account.preferences, label: 'Preferences', icon: Settings },
];

function greeting(d = new Date()) {
  const h = d.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export default function HomePage() {
  const { data: session } = useSession();
  const { data: isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const firstName = ((session?.user as { name?: string } | undefined)?.name ?? '').split(/\s+/)[0];
  const live = MODULES.filter((m) => m.status === 'live');
  const planned = MODULES.filter((m) => m.status === 'planned');

  // Every reachable page, for "jump to" — the same index as the header's page search.
  const destinations = useMemo(() => pageDestinations(!!isAdmin), [isAdmin]);
  const results = useMemo(() => searchPages(destinations, q, 12), [q, destinations]);

  useEffect(() => { setCursor(0); }, [q]);

  // "/" focuses the jump box from anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) && !t.isContentEditable) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const today = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="relative">
      {/* A faint brand wash behind the hero — decoration only. */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-brand-soft/60 to-transparent" />

      <div className="relative mx-auto w-full max-w-[1400px] px-4 pb-12 pt-8 md:px-6 md:pt-12">
        {/* Hero */}
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:justify-between">
          <div className="flex items-center gap-4">
            <CosmosMark size={52} className="hidden shrink-0 sm:block" />
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{today}</div>
              <h1 className="mt-1 text-[26px] font-[650] tracking-[-0.02em] text-foreground md:text-[30px]">
                {greeting()}{firstName ? `, ${firstName}` : ''}
              </h1>
              <p className="mt-0.5 text-sm text-muted-foreground">Where to? Every Cosmos module, one click away.</p>
            </div>
          </div>

          {/* Jump to */}
          <div className="relative w-full md:w-[420px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={input}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
                if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
                if (e.key === 'Enter' && results[cursor]) navigate(results[cursor].to);
                if (e.key === 'Escape') setQ('');
              }}
              placeholder="Jump to a page — e.g. “patching”, “my tickets”"
              aria-label="Jump to a page"
              className="h-11 w-full rounded-lg border border-border bg-card pl-9 pr-10 text-sm text-foreground shadow-sm outline-none transition-shadow placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/30"
            />
            <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-muted px-1.5 text-[10px] text-muted-foreground sm:block">/</kbd>
          </div>
        </div>

        {q.trim() ? (
          <section className="mt-8 overflow-hidden rounded-xl border border-border bg-card shadow-sm" aria-label="Matching pages">
            {results.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-muted-foreground">No pages match “{q.trim()}”.</div>
            ) : (
              <ul className="divide-y divide-divider">
                {results.map((d, i) => (
                  <li key={`${d.to}:${d.label}`}>
                    <Link to={d.to} onMouseEnter={() => setCursor(i)}
                      className={cn('flex items-center gap-3 px-5 py-3 text-sm transition-colors', i === cursor ? 'bg-muted' : 'hover:bg-muted/50')}>
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand"><d.icon className="h-3.5 w-3.5" /></span>
                      <span className="min-w-0 flex-1 truncate">
                        <span className="font-medium text-foreground">{d.label}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{d.group} › {d.section}</span>
                      </span>
                      {i === cursor && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : (
          <>
            <HomeClients />

            {/* Live modules */}
            <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {live.map((m) => <ModuleCard key={m.id} module={m} columns={MODULE_MENUS[m.id as keyof typeof MODULE_MENUS] ?? []} />)}
            </div>

            {/* Planned */}
            {planned.length > 0 && (
              <>
                <h2 className="mb-3 mt-10 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Coming to Cosmos</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {planned.map((m) => (
                    <Link key={m.id} to={m.to}
                      className="group flex gap-4 rounded-xl border border-dashed border-border bg-card/60 p-5 transition-colors hover:border-ring hover:bg-card">
                      <ModuleIcon module={m} muted />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-semibold text-foreground">{m.label}</span>
                          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Soon</span>
                        </div>
                        <div className="text-xs text-muted-foreground">{m.vendor} · {m.blurb}</div>
                        {m.planned && (
                          <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                            {m.planned.slice(0, 3).map((p) => <li key={p}>· {p}</li>)}
                          </ul>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}

            {/* Account + admin */}
            <div className="mt-10 grid grid-cols-1 items-start gap-4 md:grid-cols-2">
              <UtilityCard title="Your account" icon={<User className="h-4 w-4" />}>
                {ACCOUNT_LINKS.map((l) => <UtilityLink key={l.to} to={l.to}>{l.label}</UtilityLink>)}
              </UtilityCard>
              {isAdmin && (
                <UtilityCard title="Admin" icon={<ShieldCheck className="h-4 w-4" />}>
                  <div className="grid w-full grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                    {ADMIN_SECTIONS.map((sec) => (
                      <div key={sec.title} className="flex flex-wrap items-center gap-1.5">
                        <span className="w-full text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{sec.title}</span>
                        {sec.items.map((l) => <UtilityLink key={l.to} to={l.to}>{l.label}</UtilityLink>)}
                      </div>
                    ))}
                  </div>
                </UtilityCard>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ModuleIcon({ module: m, small, muted }: { module: ModuleDef; small?: boolean; muted?: boolean }) {
  return (
    <span className={cn('flex shrink-0 items-center justify-center rounded-lg',
      small ? 'h-7 w-7' : 'h-11 w-11',
      muted ? 'bg-muted text-muted-foreground' : 'bg-brand-soft text-brand')}>
      <m.icon className={small ? 'h-3.5 w-3.5' : 'h-5 w-5'} />
    </span>
  );
}

function ModuleCard({ module: m, columns }: { module: ModuleDef; columns: NavGroup[] }) {
  return (
    <section className="flex flex-col rounded-xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md">
      <Link to={m.to} className="group flex items-center gap-4 border-b border-divider px-5 py-4">
        <ModuleIcon module={m} />
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold tracking-[-0.01em] text-foreground">{m.label}</div>
          <div className="truncate text-xs text-muted-foreground">{m.vendor} · {m.blurb}</div>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
      </Link>
      {/* Two groups get two wide columns, so long labels ("Computers without Huntress") aren't cut off. */}
      <div className={cn('grid flex-1 grid-cols-2 gap-x-6 gap-y-4 px-5 py-4', columns.length > 2 && 'sm:grid-cols-3')}>
        {columns.map((col) => (
          <div key={col.title} className="min-w-0">
            <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{col.title}</div>
            <ul className="space-y-0.5">
              {col.items.map((i) => (
                <li key={i.label}>
                  {i.kind === 'link' ? (
                    <Link to={i.to} title={i.label} className="-mx-1.5 block truncate rounded px-1.5 py-1 text-sm text-foreground transition-colors hover:bg-muted">{i.label}</Link>
                  ) : (
                    <span className="-mx-1.5 flex items-center gap-1.5 px-1.5 py-1 text-sm text-muted-foreground">
                      <span className="truncate">{i.label}</span><span className="shrink-0 rounded-full bg-muted px-1.5 text-[9.5px] font-semibold uppercase tracking-wider">Soon</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function UtilityCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card px-5 py-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{icon}{title}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </section>
  );
}

function UtilityLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="rounded-md border border-border px-2.5 py-1 text-sm text-foreground transition-colors hover:border-ring hover:bg-muted">{children}</Link>
  );
}

// ---------------- Clients (with tier) ----------------

const CLIENTS_SHOWN = 30;

/** Every client Cosmos knows (company index), Enterprise → Premium → Standard, each opening its Orbit. */
function HomeClients() {
  const { data, isLoading } = useCompanies();
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const companies = useMemo(() => [...(data?.companies ?? [])]
    .filter((c) => c.id !== 0)
    .sort((a, b) => (a.tier ? TIER_ORDER.indexOf(a.tier) : 9) - (b.tier ? TIER_ORDER.indexOf(b.tier) : 9) || a.name.localeCompare(b.name)), [data]);
  const count = (t: CompanyLinks['tier']) => companies.filter((c) => c.tier === t).length;
  const s = q.trim().toLowerCase();
  const matches = s ? companies.filter((c) => c.name.toLowerCase().includes(s)) : companies;
  const shown = all || s ? matches : matches.slice(0, CLIENTS_SHOWN);
  if (!isLoading && companies.length === 0) return null;
  return (
    <section className="mt-8 rounded-xl border border-border bg-card px-5 py-4 shadow-sm" aria-label="Clients">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold text-foreground">Clients</h2>
        <span className="flex items-center gap-3 text-xs text-muted-foreground">
          {TIER_ORDER.map((t) => (
            <span key={t} className="inline-flex items-center gap-1"><TierBox tier={t} size="xs" /> {count(t)} {TIER_LABEL[t]}</span>
          ))}
        </span>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter clients…"
          className="ml-auto h-8 w-56 rounded-md border border-input bg-background px-2.5 text-sm text-foreground placeholder:text-muted-foreground" />
      </div>
      {isLoading ? <p className="mt-3 text-xs text-muted-foreground">Loading clients…</p> : (
        <>
          <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((c) => (
              <Link key={c.id} to={companyPath(c.id)} className="flex min-w-0 items-center gap-2 rounded-md px-2 py-1 text-sm text-foreground hover:bg-muted">
                <span className="inline-flex w-4 shrink-0 justify-center"><TierBox tier={c.tier} size="xs" /></span>
                <span className="truncate">{c.name}</span>
              </Link>
            ))}
            {shown.length === 0 && <p className="text-xs text-muted-foreground">No clients match.</p>}
          </div>
          {!s && matches.length > CLIENTS_SHOWN && (
            <button type="button" onClick={() => setAll((v) => !v)} className="mt-2 text-xs text-primary hover:underline">
              {all ? 'Show fewer' : `Show all ${matches.length} clients`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
